import PropTypes from 'prop-types';
import { Component } from 'react';
import { Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import _ from 'underscore';

import { getLicense } from '../../../api/licenses';
import { getRevisionDeepLicenses, getRevisionLicenses } from '../../../api/revisions';
import ProjectStore from '../../../stores/ProjectStore';
import COLORS from '../../../theme/colors';
import { styles as theme } from '../../../theme/mui-theme';
import type { IAsync, IssueType, License, Project, Revision, RouterProps } from '../../../types';
import { isDeclaredLicense, nuc } from '../../../utils';
import Icon, { IconText, icons } from '../../generic/Icon';
import Link, { RouterLink } from '../../generic/Link';
import Loader from '../../generic/Loader';
import LicenseDetail from '../../licenses/LicenseDetail';
import IssueListItem from '../issues/IssueListItem';
import type { ProjectDashboardProject, RevisionRouteParams } from '../projectDashboardTypes';

type ProjectPreviewProps = RouterProps<RevisionRouteParams> & {
  revision: IAsync<Revision>;
  project: ProjectDashboardProject;
};

type ProjectPreviewState = {
  project: IAsync<Project | {}>;
  licenses: {
    status: {
      loading: boolean;
    };
    selected: License;
    all: License[];
  };
};

export default class ProjectPreview extends Component<ProjectPreviewProps, ProjectPreviewState> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  static propTypes = {
    project: PropTypes.object.isRequired,
    revision: PropTypes.object.isRequired,
    params: PropTypes.object,
  };

  private unsubscribeProject: () => void;

  constructor(props) {
    super(props);
    this.state = {
      project: {
        status: {
          loading: true,
          errors: [],
        },
        data: {},
      },
      licenses: {
        status: {
          loading: true,
        },
        selected: {} as License,
        all: [],
      },
    };
  }

  componentDidMount() {
    this.unsubscribeProject = (ProjectStore as any).listen(this.onProjectUpdate);
    this.setState({
      project: (ProjectStore as any).getProject(nuc(this.props.params.project_id)) || {
        status: { loading: true },
        data: {},
      },
      licenses: { status: { loading: true }, selected: {}, all: [] },
    } as ProjectPreviewState);

    const locator = nuc(`${this.props.params.project_id}$${this.props.params.revision_id}`);
    return Promise.all([
      getRevisionLicenses(locator),
      getRevisionDeepLicenses(locator),
    ]).then(([projectLicenses, allLicenses]) => {
      const declaredLicenseIds = projectLicenses
        .filter(license => license.matches.filter(isDeclaredLicense).length > 0)
        .map(license => license.licenseId);

      const dominantLicenseId = _.min(
        projectLicenses.filter(license => license.licenseId !== 'unknown'),
        (license) => license.matches.length,
      ).licenseId;

      const selectedLicenseId = declaredLicenseIds.length > 0 ? declaredLicenseIds[0] : dominantLicenseId;

      return getLicense(selectedLicenseId)
        .catch(() => null)
        .then(license => {
          this.setState({
            licenses: {
              status: {
                loading: false,
              },
              selected: license,
              all: allLicenses,
            },
          });
        });
    });
  }

  componentWillUnmount() {
    if (this.unsubscribeProject) {
      this.unsubscribeProject();
    }
  }

  onProjectUpdate = _ => {
    this.setState({
      project: (ProjectStore as any).getProject(nuc(this.props.params.project_id)) || {
        status: { loading: true },
        data: {},
      },
    });
  };

  render() {
    if (
      !this.props.revision ||
      !this.props.revision.data ||
      this.state.project.status.loading ||
      this.state.licenses.status.loading
    ) {
      return <Loader />;
    }

    const baseUrl = `/projects/${nuc(this.props.params.project_id)}/refs/${nuc(
      this.props.params.reference_type,
    )}/${nuc(this.props.params.reference)}/${nuc(this.props.params.revision_id)}`;

    const issueCount = this.props.revision.data.unresolved_issue_count;

    let deepestLevel = _.max(this.state.licenses.all.map(license => license.depth));
    if (!isFinite(deepestLevel)) {
      deepestLevel = 0;
    }

    return (
      <div className="container container-fluid-responsive margin-top margin-top-responsive">
        {this.renderHeader(baseUrl, deepestLevel, issueCount)}
        {this.renderPreview(baseUrl, deepestLevel, issueCount)}
        {this.renderCTA()}
      </div>
    );
  }

  renderHeader = (baseUrl, deepestLevel, issueCount: number) => {
    return (
      <div
        className="panel panel-flat panel-responsive padded animated fadeInUp"
        style={{
          marginBottom: 0,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        }}>
        <div
          className="hidden-xs hidden-sm"
          style={{
            position: 'absolute',
            top: 0,
            border: 'solid 1px rgba(200, 208, 219, 0.7)',
            borderTop: 'none',
            padding: '2px 8px 0',
            borderBottomLeftRadius: '4px',
            borderBottomRightRadius: '4px',
          }}>
          <i
            className="fossa-icon-checklist"
            style={{
              marginRight: '3px',
              marginLeft: '-2px',
            }}
          />
          <b className="inline-header">Scan summary</b>
        </div>
        <div className="animated fadeInLeft fast row">
          <div className="col-md-6">
            {issueCount > 0
              ? this.renderHeaderContentFailing(deepestLevel, issueCount)
              : this.renderHeaderContentPassing(deepestLevel)}
          </div>
          <div className="col-md-6 text-right">
            <ul className="list-inline project-preview-header-list">
              <li>
                <RouterLink
                  to={`${baseUrl}/browse/dependencies`}
                  style={{
                    color: COLORS.fossaSlateBlue,
                  }}>
                  <strong>{this.props.revision.data.dependency_count}</strong> dependencies
                  <span className="margin-left-xs" style={{ fontSize: '11px' }}>
                    <Icon icon={icons.angleDoubleRight} />
                  </span>
                </RouterLink>
              </li>
              <li>
                <RouterLink
                  to={`${baseUrl}/browse/licenses`}
                  style={{
                    color: COLORS.fossaSlateBlue,
                  }}>
                  <strong>{this.props.revision.data.license_count}</strong> licenses
                  <span className="margin-left-xs" style={{ fontSize: '11px' }}>
                    <Icon icon={icons.angleDoubleRight} />
                  </span>
                </RouterLink>
              </li>
              <li>
                <RouterLink
                  to={`${baseUrl}/browse/licenses`}
                  style={{
                    color: COLORS.fossaSlateBlue,
                  }}>
                  <strong>{this.props.revision.data.todo_count}</strong> obligations
                  <span className="margin-left-xs" style={{ fontSize: '11px' }}>
                    <Icon icon={icons.angleDoubleRight} />
                  </span>
                </RouterLink>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  renderHeaderContentFailing = (deepestLevel, issueCount) => {
    return [
      <h2 className="margin-top margin-top-responsive" style={{ color: theme.statuses.error }}>
        <i
          className="fa-solid fa-triangle-exclamation"
          style={{
            border: `solid 2px ${theme.statuses.error}`,
            borderRadius: '31px',
            fontSize: '16px',
            padding: '8px',
            marginRight: '8px',
            position: 'relative',
            top: '-2px',
          }}
        />{' '}
        License scan found {issueCount} issue{issueCount > 1 ? 's' : ''}
      </h2>,
      <span
        style={{
          color: 'rgba(85, 104, 130, 0.7)',
          position: 'relative',
          left: '52px',
          top: '-8px',
        }}>
        Last scanned <strong>4 hours ago</strong>
        <br />
        {deepestLevel > 0 ? `Found dependencies ${deepestLevel} levels deep` : ''}
      </span>,
    ];
  };

  renderHeaderContentPassing = deepestLevel => {
    return [
      <h2 className="margin-top margin-top-responsive" style={{ color: theme.statuses.success }}>
        <IconText
          icon={icons.review}
          text="License scan passed"
        />
      </h2>,
      <span
        style={{
          color: 'rgba(85, 104, 130, 0.7)',
          position: 'relative',
          left: '50px',
          top: '-8px',
        }}>
        Last scanned <strong>4 hours ago</strong>
        <br />
        {deepestLevel > 0 ? `Found dependencies ${deepestLevel} levels deep` : ''}
      </span>,
    ];
  };

  renderPreview = (baseUrl, deepestLevel, issueCount: number) => {
    const projectName = this.state.project.status.loading ? '...' : (this.state.project.data as Project).title;

    return (
      <div
        className="panel panel-flat panel-responsive padded animated fadeInUp"
        style={{
          marginBottom: 0,
          borderRadius: 0,
          borderTop: 'none',
        }}>
        <div className="row">
          {issueCount > 0 ? this.renderPreviewContentFailing() : this.renderPreviewContentPassing(deepestLevel)}
          <div className="col-md-5">
            <h3 className="margin-top margin-top-responsive">Using <b>{projectName}</b> as a dependency</h3>
            <p>
              {this.state.licenses.selected
                ? <div>
                  This project is licensed under the terms of the <b>{this.state.licenses.selected.name}</b>{' '}
                  and contains <RouterLink to={`${baseUrl}/browse/licenses`}>{this.props.revision.data.license_count - 1} other licenses</RouterLink>.
                  </div>
                : <div>
                  This project contains <RouterLink to={`${baseUrl}/browse/licenses`}>{this.props.revision.data.license_count} licenses</RouterLink>.
                  </div>}

            </p>
            <RouterLink className="btn btn-primary" to={`${baseUrl}/browse/licenses`}>View Details &raquo;</RouterLink>
            <br /><br />
            {this.state.licenses.selected
              ? <div>
                <h4>
                  About {this.state.licenses.selected.name}:
                </h4>
                <LicenseDetail license={this.state.licenses.selected} />
                </div>
              : null}
          </div>
        </div>

        <div className="text-center">
          <br className="hidden-xs" />
          <span style={{ color: 'rgba(85, 104, 130, 0.7)' }}>Scroll down for more details</span>
          <br />
          <span className="animated fadeInDown">
            <Icon icon={icons.chevronDown} />
          </span>
        </div>
      </div>
    );
  };

  renderPreviewContentFailing = () => (
    <div className="col-md-7">
      <h3 className="margin-top margin-top-responsive">Scan Details</h3>
      <ul className="project-issues-list">
        {Object.entries(this.props.revision.data.unresolved_issue_counts || {})
          .filter(([, count]) => count > 0)
          .sort(([, countA], [, countB]) => countB - countA)
          .map(([issueType, count]: [IssueType, number]) => (
            <IssueListItem
              key={issueType}
              issueType={issueType}
              count={count}
              params={this.props.params}
              revision={this.props.revision.data}
              project={this.props.project.data}
            />
          ))}
      </ul>
    </div>
  );

  renderPreviewContentPassing = deepestLevel => {
    const isLimitedScan = this.props.project.data.organization?.access_level !== 'premium';

    return [
      <div className="col-md-6" key="col1">
        <h3 className="margin-top margin-top-responsive">Scan Details</h3>
        <table className="table project-preview-table">
          <tr>
            <td>License checks performed</td>
            <td>{(this.props.revision.data.dependency_count + 1) * 3}</td>
          </tr>
          <tr>
            <td>Dependencies scanned</td>
            <td>{this.props.revision.data.dependency_count}</td>
          </tr>
          <tr>
            <td>Scan type</td>
            <td>
              {!isLimitedScan ? 'Full' :
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="upgradeScanTooltip">Owner&rsquo;s account needs to be upgraded to enable deeper scanning</Tooltip>
                }>
                <span className="badge" style={{ position: 'relative', top: '-4px' }}>Limited</span>
              </OverlayTrigger>}
            </td>
          </tr>
          <tr>
            <td>Deepest dependency level</td>
            <td>{deepestLevel}</td>
          </tr>
        </table>
      </div>,
      <div className="col-md-1" key="col2" />,
    ];
  };

  renderCTA = () => (
    <div
      className="panel panel-flat padded"
      style={{
        background: theme.backgrounds.dark,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
      }}>
      <Row>
        <Col md={6} className="text-left">
          <h1
            style={{
              color: '#e7ecf3',
              margin: 0,
              lineHeight: '56px',
            }}>
            Want FOSSA for your project?
          </h1>
        </Col>
        <Col md={6} className="text-right">
          <Link
            to="/account/github_login?ref=project_preview"
            className="btn btn-primary"
            style={{
              borderWidth: '2px',
              fontSize: '16px',
              margin: '8px',
            }}
          >
            <IconText
              icon={icons.github}
              text="Login with Github"
            />
          </Link>
          <Link
            className="btn btn-primary"
            to="/auth/register"
            style={{
              borderWidth: '2px',
              fontSize: '16px',
              margin: '8px',
            }}>
            Sign up with Email
          </Link>
        </Col>
      </Row>
    </div>
  );
}
